import React, { useState, useEffect } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  Avatar,
  Skeleton,
  Paper,
  Grid,
  Divider
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGptResponse } from '../../redux/middleware';
import { Search, MedicalServices, Settings } from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';

const heartbeat = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const rotate360 = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Styled components
const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4),
  // backgroundColor: '#121212',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2)
  }
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: '#1a75ff',
  width: theme.spacing(8),
  height: theme.spacing(8),
  marginBottom: theme.spacing(2),
  animation: `${heartbeat} 1.5s infinite`
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  // backgroundColor: '#1e1e1e',
  backgroundColor: 'transparent',
  // borderRadius: theme.shape.borderRadius,
  // boxShadow: theme.shadows[0],
  marginTop: theme.spacing(3)
  // [theme.breakpoints.down('sm')]: {
  // padding: theme.spacing(2),
  // },
}));

const MessageBox = styled(Paper)(({ bgcolor }) => ({
  backgroundColor: bgcolor,
  padding: '12px 16px',
  borderRadius: '8px',
  color: '#fff',
  marginBottom: '8px'
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  flexGrow: 1,
  marginRight: theme.spacing(2),
  // width: '50%',
  borderRadius: '16px',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.secondary.light
    },
    '&:hover fieldset': {
      borderColor: '#90caf9'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#90caf9'
    }
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(1),
    height: 'auto',
    fontSize: '0.875rem'
  }
}));

const StyledButton = styled(Button)(({ theme, rotate }) => ({
  backgroundColor: '#00ffff',
  color: 'black',
  '&:hover': {
    backgroundColor: '#495057'
  },
  ...(rotate && {
    animation: `${rotate360} 1s linear`
  })
}));

const LoaderBackdrop = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  color: '#f5f5f5'
}));

const Asst = () => {
  const [query, setQuery] = useState('');
  const [rotateButton, setRotateButton] = useState(false);
  const dispatch = useDispatch();
  const { gptData, error, loading } = useSelector(state => state.gpt);
  const user = useSelector((state) => state.user);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async () => {
    if (query.trim()) {
      setRotateButton(true);
      await dispatch(fetchGptResponse(query));
      setQuery('');
      setRotateButton(false);
    }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleKeyDown = (e) => {
    // console.log(`Key pressed: ${e.key}`); // Debugging: log the pressed key
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        e.preventDefault();
        setQuery((prev) => prev + '\n');
        // console.log('Newline added'); // Debugging: log newline action
      } else {
        e.preventDefault();
        handleSubmit();
      }
    }
  };

  const formatMessageContent = (content) => {
    // Replace tabs with four spaces
    const formattedContent = content.replace(/\t/g, '    ');
    const lines = formattedContent.split('\n'); // Split content into lines

    return lines.map((line, index) => {
      // Handling headings
      const headingMatch = line.match(/^(#+)\s*(.*)/);
      if (headingMatch) {
        const level = headingMatch[1].length; // Count the number of '#' for heading level
        const headingSize = level === 1 ? 'h4' : level === 2 ? 'h5' : level === 3 ? 'h6' : 'body1';
        return (
          <Typography
            key={index}
            variant={headingSize}
            fontWeight='bold'
            sx={{ textAlign: 'left', mb: 1 }}
          >
            {headingMatch[2]}
          </Typography>
        );
      }

      // Handling bold, italic, and combined text
      const parts = line.split(/(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*)/g);
      const formattedParts = parts.map((part, i) => {
        if (part.startsWith('***') && part.endsWith('***')) {
          return <strong key={i}><em>{part.slice(3, -3)}</em></strong>; // Bold and Italic
        } else if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>; // Bold
        } else if (part.startsWith('*') && part.endsWith('*')) {
          return <em key={i}>{part.slice(1, -1)}</em>; // Italic
        }
        return part; // Return non-bold or non-italic parts
      });

      // Handling bullet points and numbering
      const isBullet = line.trim().startsWith('- ');
      const isNumbered = /^\s*\d+\.\s/.test(line);
      const indentLevel = line.search(/\S/); // Find indentation level by searching for the first non-whitespace character

      if (isBullet || isNumbered) {
        return (
          <Typography
            key={index}
            sx={{ display: 'flex', alignItems: 'flex-start', textAlign: 'left', marginLeft: `${indentLevel * 20}px`, mb: 1 }}
          >
            {isBullet ? <Box component='span' sx={{ marginRight: 1 }} /> : <Box component='span' sx={{ marginRight: 1 }} />}
            <Box component='span'>{formattedParts}</Box>
          </Typography>
        );
      }

      // Return paragraph without additional formatting if no special case
      return (
        <Typography key={index} sx={{ textAlign: 'left', marginBottom: '8px' }}>
          {formattedParts}
        </Typography>
      );
    });
  };

  if (!user || !user.userData || !user.userData.is_authenticated) {
    return (
      <StyledContainer maxWidth='md' sx={{ marginTop: '5rem' }}>
        <Typography variant='h4' align='center' gutterBottom>
          Login Required
        </Typography>
        <Typography variant='body1' align='center' sx={{ mb: 3 }}>
          You need to be logged in to access this page.
        </Typography>
        <StyledButton
          variant='contained'
          onClick={() => window.location.href = '/auth/'}
        >
          Go to Login
        </StyledButton>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer maxWidth='lg' sx={{ marginTop: '5rem' }}>
      <Grid container spacing={4} justifyContent='center' alignItems='center'>
        <Grid item xs={12} sm={8} md={6}>
          <Box display='flex' justifyContent='center' alignItems='center' flexDirection='column'>
            <StyledAvatar>
              <MedicalServices />
            </StyledAvatar>
            <Typography
              variant='h4'
              align='center'
              gutterBottom
              sx={{ mt: 2, fontWeight: 600, color: '#e0e0e0' }}
            >
              Hello, I'm glad to be your Medical Assistant
            </Typography>
            <Typography variant='body1' align='center' sx={{ mb: 3, color: '#9e9e9e' }}>
              Ask any health-related question in your preferred language, and I’ll provide helpful advice.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <StyledPaper>
            {gptData.length === 0 && !loading && (
              <Box textAlign='center' sx={{ p: 4 }}>
                <Skeleton variant='text' width={210} height={60} />
                <Skeleton variant='text' width={180} height={30} />
              </Box>
            )}
            {gptData.map((message, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography variant='subtitle1' color='textSecondary'>My question:</Typography>
                <MessageBox elevation={3} bgcolor='#00796b'>
                  <Typography>{message.query}</Typography>
                </MessageBox>

                <Typography variant='subtitle1' color='textSecondary'>My assistant's answer:</Typography>
                <MessageBox elevation={3} bgcolor='#424242'>
                  <Box sx={{ whiteSpace: 'pre-wrap' }}>
                    {formatMessageContent(message.content)}
                  </Box>
                </MessageBox>
              </Box>
            ))}

            {loading && (
              <LoaderBackdrop>
                <Settings sx={{ fontSize: 60, animation: `${rotate360} 2s linear infinite`, color: '#f5f5f5' }} />
                <Typography variant='h6'>Processing your request...</Typography>
              </LoaderBackdrop>
            )}

            {error && (
              <LoaderBackdrop>
                <Alert severity='error'>Error: {error}</Alert>
              </LoaderBackdrop>
            )}
          </StyledPaper>
        </Grid>

        <Grid item xs={12}>
          <Box display='flex' alignItems='center'>
            <StyledTextField
              variant='outlined'
              placeholder='Type your healthcare question here...'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              size='small'
              multiline
              minRows={1}
              maxRows={4}
              width={150}
            />
            <StyledButton
              variant='contained'
              onClick={handleSubmit}
              disabled={loading}
              rotate={rotateButton}
              size='small'
            >
              {loading ? 'Wait...' : 'Ask'}
            </StyledButton>
          </Box>
        </Grid>
      </Grid>

      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity='success'>
          Your question has been submitted!
        </Alert>
      </Snackbar>
    </StyledContainer>
  );
};

export default Asst;
